import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/services/authApi';

const AuthContext = createContext();

// Session timeout: 1 hour of inactivity (in milliseconds)
const SESSION_TIMEOUT = 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  const clearAuth = useCallback(() => {
    setUser(null);
    localStorage.removeItem('auth_user');
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (_) {
      // ignore — cookie will be cleared server-side if reachable
    }
    clearAuth();
  }, [clearAuth]);

  // Validate session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Cookie is sent automatically — ask server if session is valid
        const data = await authAPI.getMe();
        setUser(data.user);
      } catch {
        // No valid session
        clearAuth();
      }
      setLoading(false);
    };

    initAuth();
  }, [clearAuth]);

  // Auto-logout on inactivity
  useEffect(() => {
    if (!user) return;

    const checkInactivity = () => {
      if (Date.now() - lastActivity > SESSION_TIMEOUT) {
        logout();
      }
    };

    const interval = setInterval(checkInactivity, 60000);
    return () => clearInterval(interval);
  }, [user, lastActivity, logout]);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());

    window.addEventListener('mousedown', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      window.removeEventListener('mousedown', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const response = await authAPI.login(username, password);

      if (!response || !response.user) {
        return {
          success: false,
          error: 'Invalid response from server',
        };
      }

      // Store user info for UI (token is in httpOnly cookie, not accessible to JS)
      localStorage.setItem('auth_user', JSON.stringify(response.user));
      setUser(response.user);
      setLastActivity(Date.now());

      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'Login failed. Please check your credentials.';
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, []);

  const googleLogin = useCallback(async (credential) => {
    try {
      const response = await authAPI.googleLogin(credential);

      if (!response || !response.user) {
        return { success: false, error: 'Invalid response from server' };
      }

      localStorage.setItem('auth_user', JSON.stringify(response.user));
      setUser(response.user);
      setLastActivity(Date.now());

      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'Google Sign-In failed. Please try again.';
      return { success: false, error: errorMessage };
    }
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    googleLogin,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
